// In-fiction copy. Dry, passive-aggressive corporate. Understatement only. The game never winks.
import type { AudioHook } from "./audio.js";

export const CORP = "DEEPHAUL INTERSTELLAR";

/** One line per hook that deserves a notice on the terminal. Absent = sound only. */
export const CORPORATE_COPY: Partial<Record<AudioHook, string>> = {
  "mission.start":      "CONTRACT ACTIVE. MAINTAIN REACTOR RESERVE FOR THE CONTRACTED DURATION.",
  "mission.won":        "CONTRACT FULFILLED. NO BONUS IS PAYABLE.",
  "mission.lost":       "ASSET LOST. DEEPHAUL INTERSTELLAR THANKS YOU FOR YOUR SERVICE.",
  "crew.join":          "CREW MEMBER LOGGED ABOARD.",
  "crew.leave":         "CREW MEMBER LOGGED OFF HULL. DUTIES REASSIGNED.",
  "crew.link.lost":     "CREW MEMBER OFF-LINK. DUTIES REMAIN.",
  "crew.link.restored": "CREW LINK RESTORED. NO TIME WAS CREDITED.",
  "reactor.low":        "RESERVE MARGIN BELOW CONTRACTUAL MINIMUM.",
  "reactor.critical":   "RESERVE CRITICAL. REFER TO TRAINING MATERIALS.",
  "reactor.scram":      "REACTOR SCRAM. THIS EVENT HAS BEEN LOGGED.",
  "breaker.trip":       "BREAKER FAULT. RESET PROCEDURE ISSUED TO THE BRIDGE.",
  "breaker.wrong":      "PROCEDURE DEVIATION. PANEL LOCKED PENDING REVIEW.",
  "breaker.reset":      "BREAKER RESET ACCEPTED.",
  "hold.waiting":       "SURGE DUMP REQUIRES TWO OPERATORS. THIS IS NOT OPTIONAL.",
  "hold.engaged":       "SURGE DUMP IN PROGRESS. DO NOT RELEASE HANDLES.",
  "hold.success":       "SURGE DUMP COMPLETE. CAPACITOR CHARGE RECOVERED.",
  "hold.fail":          "CAPACITOR BACKFLOW. THIS IS A BILLABLE EVENT.",
  "hold.ready":         "SURGE DUMP AVAILABLE.",
  "intercom.peer.lost": "INTERCOM CHANNEL DROPPED. CREW ARE REMINDED THAT SHOUTING IS NOT A PROTOCOL.",
  "power.brownout":     "REACTOR OUTPUT BELOW ALLOCATION. ALLOCATION HAS BEEN REDUCED ON YOUR BEHALF.",
  "o2.low":             "LIFE SUPPORT MARGIN BELOW CONTRACTUAL MINIMUM. BREATHE RESPONSIBLY.",
  "o2.critical":        "LIFE SUPPORT CRITICAL. THIS IS NOT A DRILL. IT IS ALSO NOT COVERED.",
  "o2.restored":        "LIFE SUPPORT NOMINAL.",
  "coolant.leak":       "COOLANT LEAK, LOOP 2. HOLD BYPASS VALVES A (REACTOR CONTROL) AND B (BREAKER BAY) UNTIL SEALED.",
  "coolant.sealed":     "COOLANT LOOP SEALED. THE PUDDLE IS NOT A COMPANY CONCERN.",
  "coolant.backflow":   "BYPASS RELEASED EARLY. COOLANT LOSS HAS BEEN LOGGED AGAINST THE CREW.",
  "relay.damaged":      "INTERCOM RELAY DAMAGED. SPARE IN STORAGE. SOCKET AT BRIDGE CONSOLE.",
  "relay.taken":        "SPARE RELAY CHECKED OUT. RETURN THE OLD ONE FOR DEPOSIT.",
  "relay.socketed":     "INTERCOM RELAY REPLACED. DEPOSIT FORFEITED.",
  "drill.start":        "MANDATORY PRE-DEPARTURE DRILL 4-A: PERFORM ONE CAPACITOR SURGE DUMP. TWO OPERATORS: REACTOR CONTROL + BRIDGE.",
  "drill.complete":     "DRILL 4-A LOGGED. THE COMPANY THANKS YOU IN ADVANCE OF ANY INCIDENT.",
};

/** The corporate OS's standing advice line: the highest-value available action, dryly. */
export const PLATE_COPY = {
  procedure: "RESET PROCEDURE PRINTED ON THE INSTRUMENT OVERLAY. SIT BACK TO READ IT.",
  header: "INSTRUMENT OVERLAY 114-C",
  footer: "DO NOT CLEAN WITH SOLVENT · REPLACE IF CRAZED",
} as const;

/**
 * The pane's message deck. It cycles when nothing urgent is printed on it — company notices,
 * contract clauses, safety reminders. Deliberately dry; refine and extend freely.
 */
export const PLATE_NOTICES = [
  "REMINDER: UNSCHEDULED HEROISM IS NOT COVERED BY THE CREW POLICY.",
  "CLAUSE 14.2: TIME SPENT AWAITING A SECOND OPERATOR IS UNPAID.",
  "THE COMPANY VALUES YOUR SAFETY WITHIN REASONABLE OPERATING MARGINS.",
  "HULL CERTIFICATE: EXPIRED. GRACE PERIOD APPLIES UNTIL ARRIVAL.",
  "REPORT ANY UNFAMILIAR SOUND. DO NOT INVESTIGATE IT ALONE.",
  "CLAUSE 9.1: THE COMPANY RETAINS TITLE TO ALL RECOVERED MATERIAL.",
  "COFFEE IS PROVIDED AT COMPANY EXPENSE. CUPS ARE NOT.",
  "IN THE UNLIKELY EVENT OF A CATEGORY 4 VENT ANOMALY, REMAIN SEATED.",
  "PERSONNEL ARE REMINDED THAT THE AIRLOCK IS NOT A DISPOSAL CHUTE.",
  "MORALE IS A SHARED RESPONSIBILITY. YOURS IS PENDING REVIEW.",
  "CLAUSE 22: SLEEP IS PERMITTED DURING UNCOMPENSATED HOURS ONLY.",
  "THIS OVERLAY IS FOR REFERENCE. THE COMPANY'S RECORDS TAKE PRECEDENCE.",
  "IF THIS PANEL FLICKERS, THE PANEL IS NOT THE PROBLEM.",
  "DEEPHAUL INTERSTELLAR: WE GET IT THERE, USUALLY.",
] as const;

export const ADVISORY = {
  drill:        "ADVISORY: DRILL 4-A PENDING. SURGE DUMP: HANDLE A (REACTOR CONTROL) + HANDLE B (BRIDGE), HELD TOGETHER 5 s.",
  breaker:      "ADVISORY: BREAKER FAULT IS MULTIPLYING DRAIN. PROCEDURE IS ISSUED TO THE BRIDGE; SWITCHES ARE IN THE BREAKER BAY.",
  coolant:      "ADVISORY: COOLANT LEAK IS MULTIPLYING DRAIN. VALVES: A (REACTOR CONTROL) + B (BREAKER BAY), HELD TOGETHER.",
  relay:        "ADVISORY: INTERCOM RELAY DOWN. SPARE IN STORAGE (LADDER FROM REACTOR CONTROL); SOCKET AT BRIDGE.",
  o2:           "ADVISORY: LIFE SUPPORT IS UNDER-POWERED. REALLOCATE AT REACTOR CONTROL BEFORE BREATHING BECOMES BILLABLE.",
  dumpReady:    "ADVISORY: CAPACITOR SURGE DUMP AVAILABLE (+25 % RESERVE). HANDLE A (REACTOR CONTROL) + HANDLE B (BRIDGE).",
  cooling:      "ADVISORY: REACTOR COOLING IS UNDER-POWERED AND ADDING DRAIN. REALLOCATE AT REACTOR CONTROL.",
  nominal:      "ALL SYSTEMS WITHIN CONTRACTUAL TOLERANCE. REMAIN VIGILANT AND BILLABLE.",
} as const;

export const ORIENTATION = [
  "DEEPHAUL INTERSTELLAR — CREW ORIENTATION (ABRIDGED)",
  "1. THE RESERVE DRAINS. THIS IS NORMAL. AT 0 % THE CONTRACT (AND CREW) LAPSES.",
  "2. THE SURGE DUMP RESTORES +25 %. IT REQUIRES TWO OPERATORS: HANDLE A AT REACTOR CONTROL, HANDLE B AT THE BRIDGE, HELD TOGETHER FOR 5 s.",
  "3. POWER IS ALLOCATED AT REACTOR CONTROL. THERE IS NOT ENOUGH. UNDER-COOLING SPEEDS THE DRAIN; UNDER-POWERED LIFE SUPPORT CONSUMES O2; THE INTERCOM BUS IS YOUR PICTURE AND SOUND.",
  "4. FAULTS (BREAKERS, COOLANT, RELAYS) MULTIPLY THE DRAIN OR DEGRADE THE INTERCOM UNTIL RESOLVED. INSTRUCTIONS APPEAR WHERE THE COMPANY DEEMS APPROPRIATE.",
  "5. TALK TO EACH OTHER. THE INTERCOM IS PROVIDED AT COMPANY EXPENSE.",
] as const;
export const END_COPY = {
  won:  "CONTRACT FULFILLED. HULL RETURNED IN ACCEPTABLE CONDITION. NO BONUS IS PAYABLE.",
  lost: "ASSET LOST. AN INCIDENT REPORT HAS BEEN PRE-FILLED ON YOUR BEHALF.",
} as const;

export const INTERCOM_COPY = {
  prompt: "INTERCOM: OPEN CHANNEL. CAMERA AND MICROPHONE ARE COMPANY PROPERTY WHILE ABOARD.",
  av: "OPEN INTERCOM (CAMERA + MIC)",
  audio: "AUDIO ONLY",
  none: "NO CAMERA (PORTRAIT)",
  noSignal: "NO SIGNAL",
  denied: "INTERCOM HARDWARE UNAVAILABLE. CONTINUING WITHOUT.",
  insecure: "INTERCOM REQUIRES A SECURE LINK (HTTPS).",
} as const;

export const MENU_COPY = {
  title: "CREW COMFORT & COMPLIANCE PANEL",
  form: "FORM 9 · RETAIN FOR YOUR RECORDS",
  audio: "AUDIBLE OUTPUT",
  crewAudio: "CREW CHANNEL LEVELS",
  restart: "ABANDON CONTRACT AND RE-BRIEF (FORM 9-C)",
  restartHint: "REQUIRES A MAJORITY OF LOGGED-ON CREW. THE COMPANY IS NOT OBLIGED TO ACKNOWLEDGE REGRET.",
  leave: "LOG OFF THIS HULL",
  close: "RETURN TO STATION",
} as const;

/** The dock gate: the company's idea of a doormat, shown before the crew access terminal. */
export const GATE_COPY = {
  title: "DOCKING CONTROL — RESTRICTED AREA",
  body: "THIS HULL IS BERTHED ON A PRIVATE DOCK. STATE THE CONTRACTOR CODE ISSUED WITH YOUR SHIFT ROSTER.",
  label: "CONTRACTOR CODE",
  submit: "PRESENT CREDENTIALS",
  rejected: "CODE NOT RECOGNISED. DOCKING CONTROL HAS LOGGED THIS ATTEMPT (FORM 3-B).",
  throttled: "TOO MANY ATTEMPTS. DOCKING CONTROL IS ON ITS BREAK. TRY AGAIN SHORTLY.",
  offline: "DOCKING CONTROL IS NOT ANSWERING. THE DOCK MAY BE UNCREWED.",
  footer: "UNAUTHORISED BOARDING IS A BREACH OF CONTRACT AND, IN SOME JURISDICTIONS, OF PHYSICS.",
} as const;

export const LOBBY_COPY = {
  waiting: "AWAITING SECOND CREW MEMBER. DISTRIBUTE HULL ID BY ANY APPROVED CHANNEL.",
  reconnecting: "INTERCOM LINK DEGRADED — RE-ESTABLISHING",
  expired: "LINK EXPIRED. RE-BOARD.",
  denied: "CREDENTIALS REJECTED.",
  badCode: "HULL CODE FORMAT IS ABCD-1234 (FOUR LETTERS, FOUR DIGITS).",
  noHull: "NO HULL ON THIS CODE. CHECK WITH WHOEVER COMMISSIONED IT.",
} as const;
